import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";

import logo from "../img/swb-logo-heart.png";
import login from "../img/login-bg.png";
import axios from "../api/axios";

const LOGIN_URL = "/login";

const Login = () => {
  const navigate = useNavigate();

  const emailRef = useRef();
  const errRef = useRef();

  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");

  const [errMsg, setErrMsg] = useState("");
  const [isPending, setIsPending] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    emailRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
    setIsPending(false);
  }, [email, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsPending(false);

    try {
      const response = await axios.post(LOGIN_URL, { email, password: pwd });

      const user = response.data.result.user;
      let id = user.id;
      localStorage.setItem("login", true);
      setEmail("");
      setPwd("");
      navigate("/2fa", { state: id });
    } catch (error) {
      if (!error?.response) {
        setErrMsg("No Server Response");
      } else if (error.response?.status === 400) {
        setErrMsg("Email or Password Missing.");
      } else if (
        error.response?.status === 401 ||
        error.response?.status === 404
      ) {
        // Use the same message for both cases so we don't reveal whether an account exists
        setErrMsg("Incorrect Email or Password entered.");
      } else if (error.response?.status === 403) {
        setIsPending(true);
        setErrMsg(
          "We're still reviewing your account. We'll email you as soon as it's approved - this can take up to 2-3 working days.",
        );
      } else {
        setErrMsg("Error Occurred. Could not log you in.");
      }
      errRef.current.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center px-8 relative"
      style={{ backgroundImage: `url(${login})` }}
    >
      <div className="max-w-lg mx-auto w-full space-y-4 md:space-y-6 sm:p-8 md:absolute md:top-[18%] md:right-[20%]">
        <div>
          <div className="pt-16 md:pt-0 flex items-center gap-3 pb-2 leading-tight tracking-wider text-[#360F41] dark:text-white">
            <img src={logo} alt="SWB Logo" className="h-20 w-auto" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">
                School Wellbeing Staff Portal
              </h1>
            </div>
          </div>
          <h2 className="text-sm md:text-base font-medium">
            Welcome! To login, enter your details below.
          </h2>
        </div>

        <p
          ref={errRef}
          className={
            errMsg
              ? isPending
                ? "errmsg text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3"
                : "errmsg text-red-600 text-sm"
              : "offscreen"
          }
          aria-live="assertive"
        >
          {errMsg}
        </p>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 md:space-y-4 max-w-md mx-auto"
        >
          <div className="w-full">
            <label
              htmlFor="email"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-white"
            >
              Email
            </label>
            <input
              type="email"
              ref={emailRef}
              id="email"
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Email"
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-300 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            />
          </div>

          <div>
            <label
              htmlFor="pwd"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-white"
            >
              Password
            </label>
            <input
              type="password"
              id="pwd"
              onChange={(e) => setPwd(e.target.value)}
              required
              placeholder="Password"
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-300 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            />
          </div>
          <div>
            {loading ? (
              <div className="w-full bg-portal-light hover:bg-portal-medium text-white font-semibold py-2 px-4 rounded-lg cursor-pointer transition text-center mt-2">
                Loading...{" "}
                <FontAwesomeIcon icon={faSpinner} spin className="ml-2" />
              </div>
            ) : (
              <button className="w-full bg-portal-light hover:bg-portal-medium text-white font-semibold py-2 px-4 rounded-lg cursor-pointer transition text-center mt-2">
                Login
              </button>
            )}

            <div className=" flex justify-between items-center text-xs font-medium text-gray-500 dark:text-primary-500 mt-4">
              <Link
                to="/create-account/step-1"
                className="hover:text-violet-400"
                role="button"
              >
                Don't have an account? Sign up here
              </Link>
              <Link
                to="/forgot-password"
                className="hover:text-violet-400"
                role="button"
              >
                Forgot Password?
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
